import type {ContentPreviewDto} from '@devmil/contracts';
import {useState} from 'react';

/**
 * Prévia 9:16 da peça.
 *
 * O componente tem DOIS modos, e a diferença entre eles é o que o dono está
 * aprovando:
 *
 * 1. **Mídia real.** Quando a versão já foi renderizada, o quadro mostra a CAPA
 *    de verdade, com um botão que troca a capa pelo vídeo. É o que o Instagram
 *    vai publicar, e é o único modo em que aprovar significa ter visto a peça.
 *    A Story não tem capa: ela entra direto como vídeo, porque o renderizador
 *    só produz JPEG para o Reel.
 * 2. **Esquema.** Sem mídia renderizada (peça em rascunho, ou fonte simulada),
 *    continua valendo o desenho aprovado: marca, gancho e rodapé sobre o fundo
 *    grafite, com o círculo oliva saindo parcialmente do quadro. A geometria, o
 *    raio, o círculo e a tipografia vêm da prévia aprovada e são travados por
 *    comparação de pixel.
 *
 * O esquema NÃO foi substituído pelo real, e isso é decisão: enquanto a peça
 * não foi renderizada não existe capa nenhuma para mostrar, e um quadro vazio
 * no lugar do esquema seria a tela de aprovação piorando justamente no dia em
 * que a renderização falha.
 *
 * O modo esquema aceita DUAS entradas e desenha o mesmo quadro com as duas:
 *
 * - `ContentPreviewDto`, que é o que a fixture entrega hoje.
 * - `RenderSpec`, que é a MESMA entrada que o renderizador recebe.
 *
 * A segunda existe para a prévia e a peça publicada saírem da mesma origem.
 * Aprovar olhando uma coisa e publicar outra é o defeito que isso evita.
 *
 * O que nunca entra aqui é HTML vindo da API. A prévia monta o desenho a
 * partir de dado estruturado; receber marcação pronta de fora seria deixar o
 * servidor escrever no painel. O endereço da mídia também não é montado aqui:
 * ele vem pronto da porta `PanelDataSource`, que é o único caminho de rede do
 * painel.
 */

/** O mesmo formato que `@devmil/renderer` consome. Declarado aqui para o
 * painel não depender do pacote do renderizador, que carrega Playwright. */
export interface PreviewRenderSpec {
  pieceKind: 'reel' | 'story';
  hook: string;
  segments: ReadonlyArray<{order: number; screenText: string}>;
  cta: string;
}

/**
 * Os endereços da mídia renderizada desta peça.
 *
 * `null` nos dois campos é o caso normal enquanto a peça não foi renderizada, e
 * é também o que a fonte simulada devolve sempre. Quem decide se o endereço
 * existe é o servidor, pelo campo `media` do contrato: a tela nunca "tenta e
 * vê", porque tentar e falhar deixa um quadro quebrado na tela de aprovação.
 */
export interface PreviewMedia {
  coverUrl: string | null;
  videoUrl: string | null;
}

export type MediaPreviewProps =
  | {
      preview: ContentPreviewDto;
      spec?: never;
      state?: PreviewState;
      media?: PreviewMedia;
    }
  | {
      spec: PreviewRenderSpec;
      preview?: never;
      state?: PreviewState;
      media?: PreviewMedia;
    };

export type PreviewState =
  | {kind: 'ready'}
  | {kind: 'loading'}
  | {kind: 'empty'}
  | {kind: 'error'; onRetry: () => void};

/**
 * Traduz `RenderSpec` para os três campos que o quadro desenha.
 *
 * Se a suíte visual ficar vermelha depois de uma mudança aqui, o adaptador
 * está errado, e não a tela.
 */
const doSpec = (spec: PreviewRenderSpec): ContentPreviewDto => ({
  brand: spec.pieceKind === 'reel' ? 'DevMil Reels' : 'DevMil Stories',
  hook: spec.hook,
  footer: spec.cta,
});

const temMidia = (media: PreviewMedia | undefined): media is PreviewMedia =>
  media !== undefined && (media.coverUrl !== null || media.videoUrl !== null);

/**
 * O quadro com a mídia de verdade, e o botão que abre o vídeo.
 *
 * O botão fica ABAIXO do quadro, e não sobre ele: por cima da capa ele tapava a
 * última linha da peça, que é onde mora o CTA, na mesma tela em que o dono
 * confere o portão de CTA. As capturas da proposta mostraram isso.
 *
 * Começa na CAPA, e não no vídeo, por dois motivos. O primeiro é de custo: a
 * capa é uma imagem de dezenas de quilobytes e o vídeo é o arquivo inteiro,
 * e abrir a tela do dia não pode baixar seis vídeos que ninguém pediu. O
 * segundo é de movimento: a página tem um contrato de movimento reduzido, e um
 * vídeo que começa sozinho o quebraria.
 *
 * Sem capa (o caso da Story, que o renderizador entrega sem JPEG) o vídeo entra
 * direto, PARADO, com `preload="metadata"`. O `#t=0.001` no endereço é o que
 * faz o navegador buscar o primeiro quadro e desenhá-lo: sem ele, o elemento
 * fica preto até alguém apertar o play, e um retângulo preto não é prévia. O
 * fragmento é do cliente e nunca chega ao servidor.
 *
 * O texto do esquema (marca, gancho, rodapé) NÃO é desenhado por cima: ele já
 * está queimado dentro da peça renderizada, e repeti-lo seria mostrar ao dono
 * uma peça com duas camadas de texto que o Instagram não vai publicar.
 */
const QuadroReal = ({media}: {media: PreviewMedia}) => {
  const [mostrandoVideo, setMostrandoVideo] = useState(media.coverUrl === null);
  const video = media.videoUrl;

  return (
    <div className="dm-reel-box">
      <div
        className="dm-reel dm-reel-real"
        aria-label="Prévia renderizada da peça selecionada"
      >
        {mostrandoVideo && video !== null ? (
          <video
            className="dm-reel-media"
            src={`${video}#t=0.001`}
            controls
            preload="metadata"
            playsInline
          />
        ) : (
          <img
            className="dm-reel-media"
            src={media.coverUrl!}
            alt="Capa da peça renderizada"
          />
        )}
      </div>
      {!mostrandoVideo && video !== null ? (
        <button
          type="button"
          className="dm-reel-play"
          onClick={() => {
            setMostrandoVideo(true);
          }}
        >
          Ver vídeo
        </button>
      ) : null}
    </div>
  );
};

const Quadro = ({preview}: {preview: ContentPreviewDto}) => (
  <div className="dm-reel" aria-label="Prévia vertical da peça selecionada">
    <span className="dm-reel-brand">{preview.brand}</span>
    <strong className="dm-reel-hook">{preview.hook}</strong>
    <span className="dm-reel-foot">{preview.footer}</span>
  </div>
);

export const MediaPreview = (props: MediaPreviewProps) => {
  const estado = props.state ?? {kind: 'ready'};

  // Carregando reserva a proporção antes de ter conteúdo: sem isso a coluna
  // pula de altura quando a peça chega, e o que a pessoa ia clicar se move.
  if (estado.kind === 'loading') {
    return (
      <div
        className="dm-reel"
        aria-busy="true"
        aria-label="Carregando a prévia da peça"
      >
        <span className="dm-reel-brand">DevMil</span>
        <strong className="dm-reel-hook">Carregando…</strong>
        <span className="dm-reel-foot">aguarde</span>
      </div>
    );
  }

  if (estado.kind === 'error') {
    return (
      <div
        className="dm-reel"
        role="alert"
        aria-label="A prévia da peça não pôde ser carregada"
      >
        <span className="dm-reel-brand">DevMil</span>
        <strong className="dm-reel-hook">Prévia indisponível</strong>
        <button
          type="button"
          className="dm-reel-foot"
          onClick={estado.onRetry}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
          }}
        >
          PREVIEW_UNAVAILABLE · tentar de novo
        </button>
      </div>
    );
  }

  // Vazio é só quando a versão ainda não foi renderizada. Não é o estado de
  // erro disfarçado: dizer "sem prévia" quando a renderização falhou esconderia
  // uma falha que alguém precisa ver.
  if (estado.kind === 'empty') {
    return (
      <div
        className="dm-reel"
        aria-label="Esta versão ainda não foi renderizada"
      >
        <span className="dm-reel-brand">DevMil</span>
        <strong className="dm-reel-hook">Ainda não renderizada</strong>
        <span className="dm-reel-foot">sem mídia</span>
      </div>
    );
  }

  if (estado.kind === 'ready' && temMidia(props.media)) {
    // A `key` é o endereço da mídia, e não é enfeite: sem ela, trocar de peça
    // com o vídeo aberto reaproveitaria a mesma instância e o quadro
    // continuaria em modo vídeo, agora apontando para outra peça. O dono
    // olharia o vídeo da peça nova achando que ainda está na antiga.
    return (
      <QuadroReal
        key={props.media.coverUrl ?? props.media.videoUrl}
        media={props.media}
      />
    );
  }

  return <Quadro preview={props.spec ? doSpec(props.spec) : props.preview!} />;
};
